import { type Static, type TObject, Type as t } from '@sinclair/typebox';
import { CategorySchema } from '../game/categories';
import { LocaleSchema } from '../i18n/locale';
import { MugshotSchema, PlayerSchema, PresenceSchema, RoleSchema } from './player';
import { PhaseSchema, RoomSchema, VoteSchema } from './room';

const Tagged = <K extends string, P extends TObject>(kind: K, payload: P) =>
  t.Composite([t.Object({ kind: t.Literal(kind) }), payload]);

/**
 * `manual` = PromoteHost; `auto-disconnect` = server-driven on lobby close
 * (no grace); `auto-grace-expired` = mid-game grace timer expired.
 */
export const HostChangeReason = {
  Manual: 'manual',
  AutoDisconnect: 'auto-disconnect',
  AutoGraceExpired: 'auto-grace-expired',
} as const;
export type HostChangeReason = (typeof HostChangeReason)[keyof typeof HostChangeReason];

export const HostChangeReasonSchema = t.Union(
  [
    t.Literal(HostChangeReason.Manual),
    t.Literal(HostChangeReason.AutoDisconnect),
    t.Literal(HostChangeReason.AutoGraceExpired),
  ],
  { $id: 'HostChangeReason' },
);

/**
 * Stable codes for engine rejections. Clients map code → localized message;
 * `message` (server-provided) is debug-only fallback.
 */
export const ErrorCode = {
  // Engine reductions
  Unauthorized: 'unauthorized',
  RoomAlreadyExists: 'room_already_exists',
  RoomFull: 'room_full',
  WrongPhase: 'wrong_phase',
  OnlyHost: 'only_host',
  NotEnoughPlayers: 'not_enough_players',
  TooManyPlayers: 'too_many_players',
  InvalidTarget: 'invalid_target',
  NotYourGuess: 'not_your_guess',
  NotAMember: 'not_a_member',
  UnknownPlayer: 'unknown_player',
  NameRequired: 'name_required',
  Internal: 'internal',
  // Protocol / transport
  NoSession: 'no_session',
  InvalidMessage: 'invalid_message',
  CreateViaRest: 'create_via_rest',
  ActorMismatch: 'actor_mismatch',
  SystemOnlyMessage: 'system_only_message',
  RoomNotFound: 'room_not_found',
} as const;
export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

export const ErrorCodeSchema = t.Union(
  [
    t.Literal(ErrorCode.Unauthorized),
    t.Literal(ErrorCode.RoomAlreadyExists),
    t.Literal(ErrorCode.RoomFull),
    t.Literal(ErrorCode.WrongPhase),
    t.Literal(ErrorCode.OnlyHost),
    t.Literal(ErrorCode.NotEnoughPlayers),
    t.Literal(ErrorCode.TooManyPlayers),
    t.Literal(ErrorCode.InvalidTarget),
    t.Literal(ErrorCode.NotYourGuess),
    t.Literal(ErrorCode.NotAMember),
    t.Literal(ErrorCode.UnknownPlayer),
    t.Literal(ErrorCode.NameRequired),
    t.Literal(ErrorCode.Internal),
    t.Literal(ErrorCode.NoSession),
    t.Literal(ErrorCode.InvalidMessage),
    t.Literal(ErrorCode.CreateViaRest),
    t.Literal(ErrorCode.ActorMismatch),
    t.Literal(ErrorCode.SystemOnlyMessage),
    t.Literal(ErrorCode.RoomNotFound),
  ],
  { $id: 'ErrorCode' },
);

export const ClientMsgKind = {
  CreateRoom: 'CreateRoom',
  JoinRoom: 'JoinRoom',
  LeaveRoom: 'LeaveRoom',
  StartGame: 'StartGame',
  AcknowledgeWord: 'AcknowledgeWord',
  OpenVote: 'OpenVote',
  CastVote: 'CastVote',
  ClochardGuess: 'ClochardGuess',
  NextRound: 'NextRound',
  PromoteHost: 'PromoteHost',
  EliminatePlayer: 'EliminatePlayer',
  RequestNextRound: 'RequestNextRound',
  UpdateRoomOptions: 'UpdateRoomOptions',
  UpdateMugshot: 'UpdateMugshot',
  UpdateName: 'UpdateName',
  Pong: 'Pong',
  /** System-only — never sent by clients; routed via PresenceService → handleSystemMsg. */
  MarkPresence: 'MarkPresence',
  /** Ephemeral emoji reaction. Engine treats it as a pure broadcast — no state change. */
  Reaction: 'Reaction',
} as const;
export type ClientMsgKind = (typeof ClientMsgKind)[keyof typeof ClientMsgKind];

export const ClientMsgSchema = t.Union(
  [
    Tagged(
      ClientMsgKind.CreateRoom,
      t.Object({
        actorPlayerId: t.String(),
        hostName: t.String({ minLength: 1, maxLength: 32 }),
        hostMugshot: t.Optional(MugshotSchema),
        options: t.Object({
          includeClochard: t.Boolean(),
          locale: LocaleSchema,
          categories: t.Array(CategorySchema, { minItems: 1 }),
          discussionSeconds: t.Integer(),
        }),
      }),
    ),
    Tagged(
      ClientMsgKind.JoinRoom,
      t.Object({
        actorPlayerId: t.String(),
        code: t.String(),
        name: t.String({ minLength: 1, maxLength: 32 }),
        mugshot: t.Optional(MugshotSchema),
      }),
    ),
    Tagged(ClientMsgKind.LeaveRoom, t.Object({ actorPlayerId: t.String() })),
    Tagged(ClientMsgKind.StartGame, t.Object({ actorPlayerId: t.String() })),
    Tagged(ClientMsgKind.AcknowledgeWord, t.Object({ actorPlayerId: t.String() })),
    Tagged(ClientMsgKind.OpenVote, t.Object({ actorPlayerId: t.String() })),
    Tagged(
      ClientMsgKind.CastVote,
      t.Object({ actorPlayerId: t.String(), targetPlayerId: t.String() }),
    ),
    Tagged(ClientMsgKind.ClochardGuess, t.Object({ actorPlayerId: t.String(), guess: t.String() })),
    Tagged(ClientMsgKind.NextRound, t.Object({ actorPlayerId: t.String() })),
    Tagged(
      ClientMsgKind.PromoteHost,
      t.Object({
        actorPlayerId: t.String(),
        newHostId: t.String(),
        /** Defaults to `manual` from clients; server uses auto-* variants. */
        reason: t.Optional(HostChangeReasonSchema),
      }),
    ),
    Tagged(
      ClientMsgKind.EliminatePlayer,
      t.Object({ actorPlayerId: t.String(), targetPlayerId: t.String() }),
    ),
    Tagged(ClientMsgKind.RequestNextRound, t.Object({ actorPlayerId: t.String() })),
    Tagged(
      ClientMsgKind.UpdateMugshot,
      t.Object({ actorPlayerId: t.String(), mugshot: MugshotSchema }),
    ),
    Tagged(
      ClientMsgKind.UpdateName,
      t.Object({
        actorPlayerId: t.String(),
        name: t.String({ minLength: 1, maxLength: 32 }),
      }),
    ),
    Tagged(
      ClientMsgKind.Pong,
      t.Object({
        actorPlayerId: t.String(),
        seq: t.Integer({ minimum: 0 }),
      }),
    ),
    Tagged(
      ClientMsgKind.MarkPresence,
      t.Object({
        actorPlayerId: t.String(),
        presence: PresenceSchema,
        lastSeenAt: t.Number(),
      }),
    ),
    Tagged(
      ClientMsgKind.Reaction,
      t.Object({
        actorPlayerId: t.String(),
        // Single grapheme cluster in practice (👀 / 🤨 / 😂 / 🔥 / 💀 / 🤐).
        // Cap kept tight so a malicious client can't broadcast giant strings.
        emoji: t.String({ minLength: 1, maxLength: 16 }),
      }),
    ),
    Tagged(
      ClientMsgKind.UpdateRoomOptions,
      t.Object({
        actorPlayerId: t.String(),
        options: t.Object({
          includeClochard: t.Optional(t.Boolean()),
          locale: t.Optional(LocaleSchema),
          categories: t.Optional(t.Array(CategorySchema, { minItems: 1 })),
          discussionSeconds: t.Optional(t.Integer()),
          infiltreCount: t.Optional(t.Integer({ minimum: 1, maximum: 10 })),
          hideCode: t.Optional(t.Boolean()),
        }),
      }),
    ),
  ],
  { $id: 'ClientMsg' },
);
export type ClientMsg = Static<typeof ClientMsgSchema>;

export const ServerMsgKind = {
  RoomState: 'RoomState',
  PlayerJoined: 'PlayerJoined',
  PlayerLeft: 'PlayerLeft',
  GameStarted: 'GameStarted',
  WordAssigned: 'WordAssigned',
  PhaseChanged: 'PhaseChanged',
  VoteCast: 'VoteCast',
  PlayerEliminated: 'PlayerEliminated',
  GameEnded: 'GameEnded',
  HostChanged: 'HostChanged',
  NextRoundRequested: 'NextRoundRequested',
  PresenceChanged: 'PresenceChanged',
  Heartbeat: 'Heartbeat',
  Reaction: 'Reaction',
  Error: 'Error',
} as const;
export type ServerMsgKind = (typeof ServerMsgKind)[keyof typeof ServerMsgKind];

export const ServerMsgSchema = t.Union(
  [
    Tagged(ServerMsgKind.RoomState, t.Object({ room: RoomSchema })),
    Tagged(ServerMsgKind.PlayerJoined, t.Object({ player: PlayerSchema })),
    Tagged(ServerMsgKind.PlayerLeft, t.Object({ playerId: t.String() })),
    Tagged(ServerMsgKind.GameStarted, t.Object({ flicWord: t.String(), infiltreWord: t.String() })),
    Tagged(
      ServerMsgKind.WordAssigned,
      t.Object({ playerId: t.String(), role: RoleSchema, word: t.String() }),
    ),
    Tagged(ServerMsgKind.PhaseChanged, t.Object({ phase: PhaseSchema })),
    Tagged(ServerMsgKind.VoteCast, VoteSchema),
    Tagged(ServerMsgKind.PlayerEliminated, t.Object({ playerId: t.String(), role: RoleSchema })),
    Tagged(
      ServerMsgKind.GameEnded,
      t.Object({ winner: RoleSchema, flicWord: t.String(), infiltreWord: t.String() }),
    ),
    Tagged(
      ServerMsgKind.HostChanged,
      t.Object({
        newHostId: t.String(),
        previousHostId: t.Optional(t.String()),
        reason: t.Optional(HostChangeReasonSchema),
      }),
    ),
    Tagged(
      ServerMsgKind.PresenceChanged,
      t.Object({
        playerId: t.String(),
        presence: PresenceSchema,
        lastSeenAt: t.Optional(t.Number()),
      }),
    ),
    Tagged(
      ServerMsgKind.Heartbeat,
      t.Object({
        seq: t.Integer({ minimum: 0 }),
      }),
    ),
    Tagged(
      ServerMsgKind.NextRoundRequested,
      t.Object({
        playerId: t.String(),
        total: t.Integer({ minimum: 0 }),
        needed: t.Integer({ minimum: 0 }),
      }),
    ),
    Tagged(
      ServerMsgKind.Reaction,
      t.Object({
        playerId: t.String(),
        emoji: t.String({ minLength: 1, maxLength: 16 }),
        /** Server timestamp (ms). Used by clients to dedup + animate. */
        at: t.Number(),
      }),
    ),
    Tagged(
      ServerMsgKind.Error,
      t.Object({
        code: ErrorCodeSchema,
        message: t.Optional(t.String()),
        /** Phase at error time — lets clients tag analytics without lookups. */
        phase: t.Optional(PhaseSchema),
      }),
    ),
  ],
  { $id: 'ServerMsg' },
);
export type ServerMsg = Static<typeof ServerMsgSchema>;

export type ClientMsgOf<K extends ClientMsgKind> = Extract<ClientMsg, { kind: K }>;
